// ===========================================================================
// The image lightbox.
//
// Shared images used to be thumbnails in the transcript and nothing more: to
// see one properly you had to squint at it in the chat column. Clicking one now
// opens it full-size, and every other image in the same conversation is one
// arrow away -- because an image is rarely looked at alone, and scrolling the
// chat back to find the previous one is not looking, it is hunting.
//
// The set is read from the DOM at OPEN time rather than kept in a list, so it
// is always exactly what the conversation currently holds: a message that was
// unsent, or a file someone removed from their own screen, is simply not there.
// ASCII only.
// ===========================================================================

import { t } from '../i18n.js';
import { el } from '../dom.js';

let openBox = null;

/**
 * Open the lightbox on `img`, browsable across every image inside `scope`.
 * @param {HTMLImageElement} img the image that was clicked
 * @param {HTMLElement} scope the message list to gather siblings from
 */
export function openLightbox(img, scope) {
  close(); // never stack two

  const all = [...scope.querySelectorAll('img.chat-image')].filter((n) => n.src);
  let index = Math.max(0, all.indexOf(img));

  const view = el('img', { class: 'lightbox-img', alt: img.alt || 'Shared image' });
  const counter = el('div', { class: 'lightbox-count' });
  const prev = el('button', { class: 'lightbox-nav prev', 'aria-label': 'Previous image', text: '<' });
  const next = el('button', { class: 'lightbox-nav next', 'aria-label': 'Next image', text: '>' });
  const closeBtn = el('button', { class: 'lightbox-close', 'aria-label': 'Close', text: t('Close') });

  function show(i) {
    if (!all.length) return;
    // Wrap around: at the last image, Next returns to the first. With a handful
    // of images, hitting a dead end is more annoying than looping.
    index = (i + all.length) % all.length;
    view.src = all[index].src;
    view.alt = all[index].alt || 'Shared image';
    counter.textContent = all.length > 1 ? `${index + 1} of ${all.length}` : '';
    const many = all.length > 1;
    prev.classList.toggle('hidden', !many);
    next.classList.toggle('hidden', !many);
  }

  prev.addEventListener('click', (e) => { e.stopPropagation(); show(index - 1); });
  next.addEventListener('click', (e) => { e.stopPropagation(); show(index + 1); });
  closeBtn.addEventListener('click', close);

  const box = el('div', { class: 'lightbox', role: 'dialog', 'aria-modal': 'true' }, [
    el('div', { class: 'lightbox-bar' }, [counter, closeBtn]),
    prev,
    view,
    next,
  ]);
  // Clicking the backdrop closes; clicking the picture itself does not, so a
  // mis-aimed tap while browsing does not throw you out.
  box.addEventListener('click', (e) => {
    if (e.target === box) close();
  });

  function onKey(e) {
    if (e.key === 'Escape') close();
    else if (e.key === 'ArrowLeft') show(index - 1);
    else if (e.key === 'ArrowRight') show(index + 1);
  }
  document.addEventListener('keydown', onKey);

  // Swipe, because this is used on a phone more than anywhere else.
  let touchX = null;
  box.addEventListener('touchstart', (e) => { touchX = e.changedTouches[0]?.clientX ?? null; }, { passive: true });
  box.addEventListener('touchend', (e) => {
    if (touchX == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? touchX) - touchX;
    if (Math.abs(dx) > 45) show(index + (dx < 0 ? 1 : -1));
    touchX = null;
  }, { passive: true });

  openBox = { box, onKey };
  document.body.append(box);
  show(index);
  closeBtn.focus();
}

export function close() {
  if (!openBox) return;
  document.removeEventListener('keydown', openBox.onKey);
  openBox.box.remove();
  openBox = null;
}

/**
 * Make a chat image open the lightbox. Marks it so the lightbox can find its
 * siblings, and makes it reachable by keyboard -- a click target that only a
 * mouse can use is not a control.
 * @param {HTMLImageElement} img
 * @param {HTMLElement} scope the message list
 */
export function makeImageViewable(img, scope) {
  img.classList.add('chat-image');
  img.tabIndex = 0;
  img.title = 'Click to view';
  img.addEventListener('click', () => openLightbox(img, scope));
  img.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openLightbox(img, scope);
    }
  });
}

export default { openLightbox, close, makeImageViewable };
